import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import {
  IoClose,
  IoCalendarOutline,
  IoCheckmarkCircle,
  IoTimeOutline,
  IoAlertCircleOutline,
} from 'react-icons/io5';

const API_BASE_URL = "https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1";

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const RequestReviewModal = ({ open, onClose, patient, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [priority, setPriority] = useState('medium');
  const [slot1, setSlot1] = useState(null);
  const [slot2, setSlot2] = useState(null);
  const [slot3, setSlot3] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const priorities = [
    { value: 'low', label: 'Low', color: 'bg-green-100 text-green-800', icon: '🟢' },
    // { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800', icon: '🟡' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800', icon: '🔴'  },
    // { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800', icon: '🔴' },
  ];

  // Filter out past dates and times
  const filterPassedTime = (time) => {
    const currentDate = new Date();
    const selectedDate = new Date(time);
    return currentDate.getTime() < selectedDate.getTime();
  };

  const handleSubmit = async () => {
    // Validation
    if (!reason.trim()) {
      setError('Please provide a reason for the review');
      return;
    }

    if (!slot1) {
      setError('Please select at least one preferred time slot');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const requestData = {
        patient_id: patient.id,
        reason: reason.trim(),
        priority: priority,
        preferred_slot_1: slot1.toISOString(),
        preferred_slot_2: slot2 ? slot2.toISOString() : null,
        preferred_slot_3: slot3 ? slot3.toISOString() : null,
      };

      console.log('📤 Submitting review request:', requestData);

      const response = await fetch(`${API_BASE_URL}/patients/review-requests/create/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.detail || 'Failed to submit review request');
      }

      console.log('✅ Review request submitted:', data);

      setSuccess(true);

      // Close modal after 2 seconds
      setTimeout(() => {
        onSuccess && onSuccess(data.data);
        handleClose();
      }, 2000);

    } catch (err) {
      console.error('❌ Error submitting review request:', err);
      setError(err.message || 'Failed to submit review request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setPriority('medium');
    setSlot1(null);
    setSlot2(null);
    setSlot3(null);
    setError('');
    setSuccess(false);
    onClose();
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Request Medical Review
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Request an expert review for {patient?.first_name} {patient?.last_name}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              disabled={submitting}
            >
              <IoClose className="text-2xl" />
            </button>
          </div>

          {/* Success Message */}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800"
            >
              <div className="flex items-center gap-3 text-green-800 dark:text-green-300">
                <IoCheckmarkCircle className="text-3xl" />
                <div>
                  <p className="font-bold text-lg">Review request submitted successfully!</p>
                  <p className="text-sm">Our team will review your request and contact you soon.</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Form */}
          <div className="p-6 space-y-6">
            {/* Priority Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Priority Level *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {priorities.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPriority(p.value)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      priority === p.value
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="text-3xl mb-2">{p.icon}</div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white">
                      {p.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Reason */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Reason for Review *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Patient wound not healing as expected, need expert opinion on treatment approach..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Provide detailed information about why you need this review
              </p>
            </div>

            {/* Preferred Time Slots */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <IoCalendarOutline className="text-xl text-teal-500" />
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Preferred Time Slots (Select up to 3) *
                </label>
              </div>

              <div className="space-y-4">
                {/* Slot 1 */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 font-bold">
                    1
                  </div>
                  <div className="flex-1">
                    <DatePicker
                      selected={slot1}
                      onChange={(date) => setSlot1(date)}
                      showTimeSelect
                      filterTime={filterPassedTime}
                      minDate={new Date()}
                      dateFormat="MMMM d, yyyy h:mm aa"
                      placeholderText="Select first preferred time..."
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  {slot1 && (
                    <button
                      onClick={() => setSlot1(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <IoClose className="text-xl" />
                    </button>
                  )}
                </div>

                {/* Slot 2 */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-bold">
                    2
                  </div>
                  <div className="flex-1">
                    <DatePicker
                      selected={slot2}
                      onChange={(date) => setSlot2(date)}
                      showTimeSelect
                      filterTime={filterPassedTime}
                      minDate={new Date()}
                      dateFormat="MMMM d, yyyy h:mm aa"
                      placeholderText="Select second preferred time (optional)..."
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      disabled={!slot1}
                    />
                  </div>
                  {slot2 && (
                    <button
                      onClick={() => setSlot2(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <IoClose className="text-xl" />
                    </button>
                  )}
                </div>

                {/* Slot 3 */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-bold">
                    3
                  </div>
                  <div className="flex-1">
                    <DatePicker
                      selected={slot3}
                      onChange={(date) => setSlot3(date)}
                      showTimeSelect
                      filterTime={filterPassedTime}
                      minDate={new Date()}
                      dateFormat="MMMM d, yyyy h:mm aa"
                      placeholderText="Select third preferred time (optional)..."
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      disabled={!slot1}
                    />
                  </div>
                  {slot3 && (
                    <button
                      onClick={() => setSlot3(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <IoClose className="text-xl" />
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-3">
                  <IoTimeOutline className="text-blue-600 dark:text-blue-400 text-xl mt-0.5" />
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    <p className="font-semibold mb-1">Note:</p>
                    <p>Our admin team will review your preferred time slots and schedule the meeting. You'll receive a calendar invite once confirmed.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center gap-2 text-red-800 dark:text-red-300">
                  <IoAlertCircleOutline className="text-xl" />
                  <p className="text-sm font-semibold">{error}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleClose}
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || success}
              className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-lg font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Submitting...
                </>
              ) : success ? (
                <>
                  <IoCheckmarkCircle className="text-xl" />
                  Submitted!
                </>
              ) : (
                <>
                  <IoCalendarOutline className="text-xl" />
                  Submit Review Request
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default RequestReviewModal;